import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import tournamentService from '../services/tournamentService';

const Tournaments = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 1,
    limit: 12
  });
  const [searchInput, setSearchInput] = useState(''); // Separate state for search input
  const [pagination, setPagination] = useState(null);
  const searchInputRef = useRef(null); // Ref for the search input

  // Sync searchInput with filters.search on mount
  useEffect(() => {
    setSearchInput(filters.search);
  }, []); // Only run on mount

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: searchInput,
        page: 1 // Reset to page 1 when search changes
      }));
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Load tournaments data
  useEffect(() => {
    const loadTournaments = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const result = await tournamentService.getTournaments(filters);
        if (result && result.success) {
          setTournaments(result.data || []);
          setPagination(result.pagination);
        } else {
          setError('Failed to load tournaments');
          setTournaments([]);
        }
      } catch (err) {
        console.error('Error loading tournaments:', err);
        setError(err.response?.data?.message || 'Unable to load tournaments. Please check your connection and try again.');
        setTournaments([]);
      } finally {
        setLoading(false);
      }
    };

    loadTournaments();
  }, [filters]);

  // Focus search input after tournaments are loaded (except on initial load)
  useEffect(() => {
    if (!loading && searchInputRef.current && filters.search !== '') {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [loading, filters.search]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 unless we're changing page
    }));
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  const handleSearchFocus = () => {
    // Ensure the input is focused when clicked
    searchInputRef.current?.focus();
  };

  const handleTournamentClick = (tournamentId) => {
    navigate(`/tournaments/${tournamentId}`);
  };

  const handlePrizeV2Click = async (e, tournamentId) => {
    e.stopPropagation(); // Prevent card click navigation
    
    try {
      const result = await tournamentService.calculatePrizeDistributionV2(tournamentId);
      console.log('Prize Distribution V2 Result:', result);
      
      // Show alert with summary
      if (result.success && result.data) {
        const summary = result.data.summary;
        alert(
          `Prize Distribution V2 (Per-Round)\n\n` +
          `Total Pot: $${result.data.tournament.totalPot.toFixed(2)}\n\n` +
          `Saw-Offs Paid: $${summary.totalSawOffsPaid.toFixed(2)}\n` +
          `Skin Prizes Paid: $${summary.totalSkinsPaid.toFixed(2)}\n` +
          `To Users: $${summary.totalToUsers.toFixed(2)}\n` +
          `To System: $${summary.totalToSystem.toFixed(2)}\n` +
          `House Keeps: $${summary.houseKeeps.toFixed(2)}\n\n` +
          `Check browser console for detailed breakdown.`
        );
      }
    } catch (error) {
      console.error('Error calculating prize V2:', error);
      alert('Error calculating Prize Distribution V2. Check console for details.');
    }
  };

  const getStatusBadge = (status) => {
    const color = tournamentService.getStatusColor(status);
    const text = tournamentService.getStatusText(status);
    
    return (
      <span style={{
        backgroundColor: color,
        color: 'white',
        padding: 'var(--spacing-xs) var(--spacing-sm)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'bold'
      }}>
        {text}
      </span>
    );
  };

  const getProgressBar = (startDate, endDate, status) => {
    if (status === 'DRAFT' || status === 'CANCELLED') return null;
    
    const progress = tournamentService.calculateProgress(startDate, endDate);
    
    return (
      <div style={{
        width: '100%',
        height: '4px',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        marginTop: 'var(--spacing-xs)',
        border: '1px solid var(--border-primary)'
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          backgroundColor: status === 'COMPLETED' ? 'var(--info)' : (status === 'LIVE' ? '#ff6b35' : 'var(--success)'),
          transition: 'width 0.3s ease'
        }} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="proskins-content" style={{
        padding: 'var(--spacing-lg)',
        // maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh'
      }}>
        <div className="proskins-text-center proskins-text-muted" style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px'
        }}>
          <div className="proskins-loading" style={{ marginBottom: 'var(--spacing-sm)' }}></div>
          Loading tournaments...
        </div>
      </div>
    );
  }

  return (
    <div className="proskins-content" style={{
      padding: 'var(--spacing-lg)',
      // maxWidth: '1200px',
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 'var(--spacing-sm)'
        }}>
          <h1 className="proskins-section-title" style={{ margin: 0 }}>Tournaments</h1>
          <button
            className="proskins-btn proskins-btn-outline"
            onClick={() => navigate('/dashboard')}
          >
            ← Back to Dashboard
          </button>
        </div>
        {error && (
          <div className="proskins-alert proskins-alert-warning">
            {error}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="proskins-card" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div style={{
          display: 'grid',
          gap: 'var(--spacing-lg)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
        }}>
          <div>
            <label className="proskins-label">
              Status
            </label>
            <select
              className="proskins-input"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
              <option value="LIVE">Live</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="proskins-label">
              Search
            </label>
            <input
              ref={searchInputRef}
              type="text"
              className="proskins-input"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              onClick={handleSearchFocus}
              placeholder="Search tournaments..."
            />
          </div>
        </div>
      </div>

      {/* Tournament Grid */}
      {tournaments.length === 0 ? (
        <div className="proskins-card proskins-text-center" style={{ padding: 'var(--spacing-2xl)' }}>
          <h3 className="proskins-text-muted" style={{ marginBottom: 'var(--spacing-sm)' }}>No tournaments found</h3>
          <p className="proskins-text-muted">
            {filters.status || searchInput 
              ? 'Try adjusting your filters to see more tournaments.'
              : 'There are no tournaments available at the moment.'}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: 'var(--spacing-lg)',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))'
        }}>
          {tournaments.map((tournament) => (
            <div
              key={tournament.id}
              className="proskins-card"
              style={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onClick={() => handleTournamentClick(tournament.id)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
              }}
            >
              <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  <h3 className="proskins-card-title" style={{ 
                    margin: 0,
                    fontSize: 'var(--font-size-xl)',
                    lineHeight: '1.3'
                  }}>
                    {tournament.name}
                  </h3>
                  {getStatusBadge(tournament.status)}
                </div>
                {tournament.description && (
                  <p className="proskins-text-secondary" style={{ 
                    fontSize: 'var(--font-size-sm)', 
                    margin: 'var(--spacing-xs) 0 0 0',
                    lineHeight: '1.4'
                  }}>
                    {tournament.description.length > 100 
                      ? `${tournament.description.substring(0, 100)}...`
                      : tournament.description
                    }
                  </p>
                )}
              </div>

              <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: 'var(--spacing-sm)',
                  fontSize: 'var(--font-size-sm)'
                }}>
                  <div>
                    <strong className="proskins-text-primary">Start Date:</strong><br />
                    <span className="proskins-text-secondary">{tournamentService.formatDate(tournament.startDate)}</span>
                  </div>
                  <div>
                    <strong className="proskins-text-primary">End Date:</strong><br />
                    <span className="proskins-text-secondary">{tournamentService.formatDate(tournament.endDate)}</span>
                  </div>
                  <div>
                    <strong className="proskins-text-primary">Format:</strong><br />
                    <span className="proskins-text-secondary">{tournamentService.getFormatText(tournament.metadata?.tournamentFormat)}</span>
                  </div>
                  <div>
                    <strong className="proskins-text-primary">Prize Pool:</strong><br />
                    <span className="proskins-text-secondary">{tournamentService.formatCurrency(tournament.potSize)}</span>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-secondary)'
                }}>
                  <span>👥 {tournament._count.players} players</span>
                  <span>🎯 {tournament._count.bets} bets</span>
                  <span>🏌️ {tournament._count.rounds} rounds</span>
                </div>
              </div>

              <div className="proskins-text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                {tournament.maxPlayers && (
                  <div>Max Players: {tournament.maxPlayers}</div>
                )}
                <div>
                  Created by: {tournament.creator.firstName} {tournament.creator.lastName}
                </div>
              </div>


              {getProgressBar(tournament.startDate, tournament.endDate, tournament.status)}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div style={{ 
          marginTop: 'var(--spacing-2xl)', 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 'var(--spacing-sm)',
          alignItems: 'center'
        }}>
          <button
            className="proskins-btn proskins-btn-outline"
            disabled={!pagination.hasPrev}
            onClick={() => handleFilterChange('page', filters.page - 1)}
          >
            Previous
          </button>
          
          <span className="proskins-text-secondary" style={{ margin: '0 var(--spacing-md)' }}>
            Page {pagination.page} of {pagination.totalPages} 
            ({pagination.totalCount} tournaments)
          </span>
          
          <button
            className="proskins-btn proskins-btn-outline"
            disabled={!pagination.hasNext}
            onClick={() => handleFilterChange('page', filters.page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Tournaments;
